/**
 * ══════════════════════════════════════════════════════════════════════════════
 * KANGAVENA CLIENT-SIDE AI MODEL PRICING ENGINE & SPECIFICATION CATALOG
 * Authoritative financial definitions for Google Gemini & Xiaomi MiMo models
 * Covers: Input, Context Caching, Output, and Live Audio per 1,000,000 tokens
 * Real-time USD ($) & Sri Lankan Rupee (LKR Rs.) accounting
 * ══════════════════════════════════════════════════════════════════════════════
 */

export interface ModelPricingTier {
  id: string;
  displayName: string;
  provider: 'Google Gemini' | 'Xiaomi MiMo' | 'Custom';
  inputPricePerM: number;       // USD per 1M fresh input tokens
  cachedInputPricePerM: number; // USD per 1M cached input tokens (discounted)
  outputPricePerM: number;      // USD per 1M output tokens
  audioInputPricePerM?: number; // USD per 1M audio input tokens
  audioOutputPricePerM?: number;// USD per 1M audio output tokens
  contextWindow: string;
  isLiveCapable: boolean;
  category: 'Live Voice & Audio' | 'High-Speed Flash' | 'Deep Reasoning Pro' | 'Xiaomi MiMo' | 'Custom';
  description: string;
  badgeColor: string;
}

export const DEFAULT_USD_TO_LKR_RATE = 305.00;

export const AI_MODEL_CATALOG: Record<string, ModelPricingTier> = {
  // ── GOOGLE GEMINI: LIVE & THINKING AUDIO CORES ──
  'gemini-3.1-flash-live-preview': {
    id: 'gemini-3.1-flash-live-preview',
    displayName: 'Gemini 3.1 Live (Thinking Audio & Multimodal)',
    provider: 'Google Gemini',
    inputPricePerM: 0.75,
    cachedInputPricePerM: 0.075,
    outputPricePerM: 3.00,
    audioInputPricePerM: 3.00,
    audioOutputPricePerM: 12.00,
    contextWindow: '1,048,576 tokens',
    isLiveCapable: true,
    category: 'Live Voice & Audio',
    description: 'Sovereign flagship real-time audio and reasoning engine. Native bidirectional audio for SIP calls.',
    badgeColor: 'border-cyan-500/50 text-cyan-300 bg-cyan-500/10',
  },
  'gemini-3.8-live': {
    id: 'gemini-3.8-live',
    displayName: 'Gemini 3.8 Live (Ultra-Low Latency Voice Core)',
    provider: 'Google Gemini',
    inputPricePerM: 0.75,
    cachedInputPricePerM: 0.075,
    outputPricePerM: 3.00,
    audioInputPricePerM: 3.00,
    audioOutputPricePerM: 12.00,
    contextWindow: '1,048,576 tokens',
    isLiveCapable: true,
    category: 'Live Voice & Audio',
    description: 'Next-gen live bidirectional streaming audio model with sub-second response latency.',
    badgeColor: 'border-sky-500/50 text-sky-300 bg-sky-500/10',
  },

  // ── GOOGLE GEMINI: HIGH-SPEED FLASH MODELS ──
  'gemini-3.8-flash': {
    id: 'gemini-3.8-flash',
    displayName: 'Gemini 3.8 Flash (High-Speed Neural Core)',
    provider: 'Google Gemini',
    inputPricePerM: 0.15,
    cachedInputPricePerM: 0.0375,
    outputPricePerM: 0.60,
    contextWindow: '1,048,576 tokens',
    isLiveCapable: false,
    category: 'High-Speed Flash',
    description: 'Ultra fast text and image generation. Exceptional throughput for high-volume customer chats.',
    badgeColor: 'border-blue-500/50 text-blue-300 bg-blue-500/10',
  },
  'gemini-3.5-flash': {
    id: 'gemini-3.5-flash',
    displayName: 'Gemini 3.5 Flash (Balanced Multimodal)',
    provider: 'Google Gemini',
    inputPricePerM: 0.15,
    cachedInputPricePerM: 0.0375,
    outputPricePerM: 0.60,
    contextWindow: '1,048,576 tokens',
    isLiveCapable: false,
    category: 'High-Speed Flash',
    description: 'High performance balanced text, vision, and tool execution engine.',
    badgeColor: 'border-indigo-500/50 text-indigo-300 bg-indigo-500/10',
  },
  'gemini-3.5-flash-lite': {
    id: 'gemini-3.5-flash-lite',
    displayName: 'Gemini 3.5 Flash-Lite (Budget High-Efficiency)',
    provider: 'Google Gemini',
    inputPricePerM: 0.075,
    cachedInputPricePerM: 0.01875,
    outputPricePerM: 0.30,
    contextWindow: '1,048,576 tokens',
    isLiveCapable: false,
    category: 'High-Speed Flash',
    description: 'Extremely economical model designed for high-density customer support, RAG, and extraction.',
    badgeColor: 'border-teal-500/50 text-teal-300 bg-teal-500/10',
  },
  'gemini-3.1-flash-lite-preview': {
    id: 'gemini-3.1-flash-lite-preview',
    displayName: 'Gemini 3.1 Flash-Lite (Fast RAG & Memory)',
    provider: 'Google Gemini',
    inputPricePerM: 0.075,
    cachedInputPricePerM: 0.01875,
    outputPricePerM: 0.30,
    contextWindow: '1,048,576 tokens',
    isLiveCapable: false,
    category: 'High-Speed Flash',
    description: 'Optimized auxiliary model for RAG knowledge synthesis, scraping, and customer memory extraction.',
    badgeColor: 'border-emerald-500/50 text-emerald-300 bg-emerald-500/10',
  },

  // ── XIAOMI MIMO: HIGH-EFFICIENCY OPENAI-COMPATIBLE MODELS ──
  'mimo-v2.6-flash': {
    id: 'mimo-v2.6-flash',
    displayName: 'Xiaomi MiMo v2.6 Flash (Ultra-Fast Agile Reasoning)',
    provider: 'Xiaomi MiMo',
    inputPricePerM: 0.10,
    cachedInputPricePerM: 0.025,
    outputPricePerM: 0.20,
    contextWindow: '131,072 tokens',
    isLiveCapable: false,
    category: 'Xiaomi MiMo',
    description: 'Xiaomi high-speed inference engine with OpenAI-compatible endpoint. Ultra competitive pricing.',
    badgeColor: 'border-amber-500/50 text-amber-300 bg-amber-500/10',
  },
  'mimo-v2.6-pro': {
    id: 'mimo-v2.6-pro',
    displayName: 'Xiaomi MiMo v2.6 Pro (Advanced Chain-of-Thought)',
    provider: 'Xiaomi MiMo',
    inputPricePerM: 0.60,
    cachedInputPricePerM: 0.15,
    outputPricePerM: 1.80,
    contextWindow: '131,072 tokens',
    isLiveCapable: false,
    category: 'Xiaomi MiMo',
    description: 'Flagship Xiaomi AI model with deep step-by-step chain-of-thought problem solving.',
    badgeColor: 'border-orange-500/50 text-orange-300 bg-orange-500/10',
  },
  'mimo-v2.5-pro': {
    id: 'mimo-v2.5-pro',
    displayName: 'Xiaomi MiMo v2.5 Pro (Precision Neural Core)',
    provider: 'Xiaomi MiMo',
    inputPricePerM: 0.80,
    cachedInputPricePerM: 0.20,
    outputPricePerM: 2.40,
    contextWindow: '131,072 tokens',
    isLiveCapable: false,
    category: 'Xiaomi MiMo',
    description: 'High-precision reasoning model for complex tool calling and specialized business logic.',
    badgeColor: 'border-rose-500/50 text-rose-300 bg-rose-500/10',
  },
  'mimo-v2.5': {
    id: 'mimo-v2.5',
    displayName: 'Xiaomi MiMo v2.5 (Standard Conversational)',
    provider: 'Xiaomi MiMo',
    inputPricePerM: 0.40,
    cachedInputPricePerM: 0.10,
    outputPricePerM: 1.20,
    contextWindow: '65,536 tokens',
    isLiveCapable: false,
    category: 'Xiaomi MiMo',
    description: 'Standard Xiaomi AI conversational core for rapid WhatsApp text automation.',
    badgeColor: 'border-yellow-500/50 text-yellow-300 bg-yellow-500/10',
  },
};

export const DEFAULT_MODEL_PRICING: ModelPricingTier = {
  id: 'gemini-3.1-flash-live-preview',
  displayName: 'Ecosystem Standard (Gemini 3.1 Live)',
  provider: 'Google Gemini',
  inputPricePerM: 0.75,
  cachedInputPricePerM: 0.075,
  outputPricePerM: 3.00,
  audioInputPricePerM: 3.00,
  audioOutputPricePerM: 12.00,
  contextWindow: '1,048,576 tokens',
  isLiveCapable: true,
  category: 'Live Voice & Audio',
  description: 'Standard sovereign AI architecture model.',
  badgeColor: 'border-cyan-500/50 text-cyan-300 bg-cyan-500/10',
};

export function getModelPricing(modelId?: string | null): ModelPricingTier {
  if (!modelId) return DEFAULT_MODEL_PRICING;
  const clean = modelId.trim().toLowerCase();
  
  if (AI_MODEL_CATALOG[clean]) {
    return AI_MODEL_CATALOG[clean];
  }

  for (const [key, tier] of Object.entries(AI_MODEL_CATALOG)) {
    if (clean.includes(key) || key.includes(clean)) {
      return tier;
    }
  }

  return {
    id: modelId,
    displayName: `Custom Model (${modelId})`,
    provider: clean.startsWith('mimo') ? 'Xiaomi MiMo' : 'Custom',
    inputPricePerM: clean.startsWith('mimo') ? 0.40 : 0.75,
    cachedInputPricePerM: clean.startsWith('mimo') ? 0.10 : 0.075,
    outputPricePerM: clean.startsWith('mimo') ? 1.20 : 3.00,
    contextWindow: '128,000 tokens',
    isLiveCapable: clean.includes('live'),
    category: 'Custom',
    description: 'Custom tenant-assigned model configuration.',
    badgeColor: 'border-purple-400/50 text-purple-200 bg-purple-400/10',
  };
}

export function calculateTokenCostBreakdown(params: {
  modelId?: string | null;
  inputTokens: number;
  cachedTokens?: number;
  outputTokens: number;
  callDurationSeconds?: number;
  callTokens?: number;
  trainingTokens?: number;
  usdToLkrRate?: number;
}) {
  const {
    modelId,
    inputTokens = 0,
    cachedTokens = 0,
    outputTokens = 0,
    callDurationSeconds = 0,
    callTokens = 0,
    trainingTokens = 0,
    usdToLkrRate = DEFAULT_USD_TO_LKR_RATE,
  } = params;

  const rate = Number(usdToLkrRate) > 0 ? Number(usdToLkrRate) : DEFAULT_USD_TO_LKR_RATE;
  const pricing = getModelPricing(modelId);

  const cleanInput = Math.max(0, Number(inputTokens) || 0);
  const cleanCached = Math.min(cleanInput, Math.max(0, Number(cachedTokens) || 0));
  const freshInput = Math.max(0, cleanInput - cleanCached);
  const cleanOutput = Math.max(0, Number(outputTokens) || 0);

  const freshInputCostUsd = (freshInput * pricing.inputPricePerM) / 1_000_000;
  const cachedInputCostUsd = (cleanCached * pricing.cachedInputPricePerM) / 1_000_000;
  const cachingSavingsUsd = (cleanCached * (pricing.inputPricePerM - pricing.cachedInputPricePerM)) / 1_000_000;
  const outputCostUsd = (cleanOutput * pricing.outputPricePerM) / 1_000_000;

  let callCostUsd = 0;
  if (callDurationSeconds > 0 || callTokens > 0) {
    const audioInPerM = pricing.audioInputPricePerM || 3.00;
    const audioOutPerM = pricing.audioOutputPricePerM || 12.00;
    const estCallTokens = callTokens > 0 ? callTokens : (callDurationSeconds * 50);
    const estCallIn = Math.round(estCallTokens * 0.55);
    const estCallOut = Math.max(0, estCallTokens - estCallIn);
    callCostUsd = (estCallIn * audioInPerM + estCallOut * audioOutPerM) / 1_000_000;
  }

  let trainingCostUsd = 0;
  if (trainingTokens > 0) {
    const trainIn = Math.round(trainingTokens * 0.7);
    const trainOut = Math.max(0, trainingTokens - trainIn);
    trainingCostUsd = (trainIn * 0.075 + trainOut * 0.30) / 1_000_000;
  }

  const subtotalUsd = freshInputCostUsd + cachedInputCostUsd + outputCostUsd + callCostUsd + trainingCostUsd;
  const subtotalLkr = subtotalUsd * rate;
  const savingsLkr = cachingSavingsUsd * rate;

  const totalTokens = cleanInput + cleanOutput + (callTokens || 0) + (trainingTokens || 0);

  return {
    modelInfo: pricing,
    usdToLkrRate: rate,
    tokens: {
      freshInput,
      cachedInput: cleanCached,
      output: cleanOutput,
      callTokens: callTokens || Math.round(callDurationSeconds * 50),
      callDurationSeconds,
      trainingTokens,
      totalTokens,
    },
    costs: {
      freshInputCostUsd: parseFloat(freshInputCostUsd.toFixed(6)),
      cachedInputCostUsd: parseFloat(cachedInputCostUsd.toFixed(6)),
      outputCostUsd: parseFloat(outputCostUsd.toFixed(6)),
      callCostUsd: parseFloat(callCostUsd.toFixed(6)),
      trainingCostUsd: parseFloat(trainingCostUsd.toFixed(6)),
      cachingSavingsUsd: parseFloat(cachingSavingsUsd.toFixed(6)),
      totalCostUsd: parseFloat(subtotalUsd.toFixed(6)),
      totalCostLkr: parseFloat(subtotalLkr.toFixed(2)),
      totalSavingsLkr: parseFloat(savingsLkr.toFixed(2)),
    },
    ratesUsed: {
      inputPricePerM: pricing.inputPricePerM,
      cachedInputPricePerM: pricing.cachedInputPricePerM,
      outputPricePerM: pricing.outputPricePerM,
      audioInputPricePerM: pricing.audioInputPricePerM || 3.00,
      audioOutputPricePerM: pricing.audioOutputPricePerM || 12.00,
    }
  };
}

export function getAllModelsCatalog(): ModelPricingTier[] {
  return Object.values(AI_MODEL_CATALOG);
}
